import { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useNavigate } from 'react-router-dom';
import { useApiState } from 'hooks/useApiState';
import type { AcctStateType } from 'stores/atoms/acctState';
import type { ReportSearchItemType } from 'types/api/reportSearchItemType';
import { PAGE_LIMIT, buildSearchPageUrl, computeOffset, parsePageNo } from '../utils/pagination';

export interface UseWsReportSearchResult {
  initialized: boolean;
  processing: boolean;
  pageNo: number;
  contents: ReportSearchItemType[];
  hasNext: boolean;
  isExpiredPlan: boolean;
  modalCd: string | null;
  modalContentsType: number | null;
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  onModalReportDownloadOpen: (content: ReportSearchItemType) => void;
  onPrev: () => void;
  onNext: () => void;
}

export function useWsReportSearch(
  pageNoParam: string | undefined,
  company: NonNullable<AcctStateType['company']>
): UseWsReportSearchResult {
  const { postReportSearch } = useApiState();
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();

  const [initialized, setInitialized] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [pageNoState, setPageNoState] = useState(1);
  const [contents, setContents] = useState<ReportSearchItemType[]>([]);
  const [hasNext, setHasNext] = useState(false);
  const [modalCd, setModalCd] = useState<string | null>(null);
  const [modalContentsType, setModalContentsType] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const isExpiredPlan = company.plan.isExpired;

  const search = async (pageNoStr: string | undefined) => {
    const no = parsePageNo(pageNoStr);

    // ローディングの表示
    setProcessing(true);

    // コンテンツ検索 API の呼び出し
    const response = await postReportSearch({
      companyId: company.id,
      limit: PAGE_LIMIT,
      offset: computeOffset(no, PAGE_LIMIT),
    });

    // 検索結果の設定
    setContents(response.contents);
    setPageNoState(no);
    setHasNext(response.hasNext);

    // ローディングの停止
    setProcessing(false);
  };

  useEffect(
    () => {
      // pageNoParam が変わるたびに検索を実行する。
      // search は useCallback 化されていないため render ごとに参照が変わり、
      // 依存に含めると毎 render 発火する。pageNoParam の変化のみで発火する
      // 移行元の挙動を保持するため意図的に除外している。
      (async () => {
        try {
          await search(pageNoParam);
        } catch (e) {
          showBoundary(e);
        } finally {
          setInitialized(true);
        }
      })();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pageNoParam]
  );

  const onModalReportDownloadOpen = (content: ReportSearchItemType) => {
    if (isExpiredPlan) {
      // プラン（利用期間切れ）の場合は処理しない
      return;
    }
    setModalCd(content.cd);
    setModalContentsType(content.contentsTypeDiv);
    setModalOpen(true);
  };

  const onPrev = () => {
    if (pageNoState <= 1) {
      return;
    }
    navigate(buildSearchPageUrl(company.cd, pageNoState - 1));
  };

  const onNext = () => {
    if (!hasNext) {
      return;
    }
    navigate(buildSearchPageUrl(company.cd, pageNoState + 1));
  };

  return {
    initialized,
    processing,
    pageNo: pageNoState,
    contents,
    hasNext,
    isExpiredPlan,
    modalCd,
    modalContentsType,
    modalOpen,
    setModalOpen,
    onModalReportDownloadOpen,
    onPrev,
    onNext,
  };
}
